import { get, isNil } from "lodash";
import { questionnaireQuestionTypes } from "./QuestionnaireConstants";

/**
 * Event Categories - Different categories of events
 */
export const EventCategories = {
    ANSWER: 'answer'
};

/**
 * Answer Events - Answer-related interaction events
 */
export const AnswerEvents = {
    OPTION_SELECTED: 'answer.option_selected',
    TEXT_CHANGED: 'answer.text_changed',
    RATING_SELECTED: 'answer.rating_selected',
    ANSWER_LOADED: 'answer.answer_loaded'
};

/**
 * Save configurations for each question type (for answer events only)
 */
export const saveConfigurations = {
    [questionnaireQuestionTypes.MULTIPLE_CHOICE]: { immediate: true },
    [questionnaireQuestionTypes.CHECKBOX]: { immediate: true },
    [questionnaireQuestionTypes.RATING_SCALE]: { immediate: true },
    [questionnaireQuestionTypes.FREE_TEXT]: { debounce: 500 }
};

/**
 * EVENT PROCESSORS
 * All processors are simple functions that handle their own type checking internally
 */
export const eventProcessors = {
    // Load answers from server (works for all question types)
    [AnswerEvents.ANSWER_LOADED]: (question, serverData) => ({
        ...question,
        userAnswers: get(serverData, 'userAnswers', []),
        textResponse: get(serverData, 'textResponse', ''),
        ratingValue: get(serverData, 'ratingValue', null)
    }),

    // Handle option selection (Multiple Choice = single selection, Checkbox = multi-selection)
    [AnswerEvents.OPTION_SELECTED]: (question, { optionId }) => {
        const questionType = get(question, 'type');
        const currentAnswers = get(question, 'userAnswers', []); 
        if (isNil(optionId) || optionId === '') {
            console.warn('Option ID is null or empty, skipping event');
            return question;
        }

        if (questionType === questionnaireQuestionTypes.MULTIPLE_CHOICE) {
            const newAnswers = currentAnswers.includes(optionId) ? [] : [optionId];
            return {
                ...question,
                userAnswers: newAnswers,
            };
        } else if (questionType === questionnaireQuestionTypes.CHECKBOX) {
            return {
                ...question,
                userAnswers: currentAnswers.includes(optionId)
                    ? currentAnswers.filter(id => id !== optionId)
                    : [...currentAnswers, optionId],
            };
        }
        return question;
    },

    /**
     * Handles text input changes for Free Text questions.
     * Only applies the change if the question type is FREE_TEXT.
     *
     * @param {object} question - The current question object.
     * @param {object} eventData - The event data.
     * @param {string} eventData.text - The new text response.
     * @returns {object} The updated question object, or the original if the question type does not match.
     */
    [AnswerEvents.TEXT_CHANGED]: (question, { text }) => {
        const questionType = get(question, 'type');
        if (questionType === questionnaireQuestionTypes.FREE_TEXT && !isNil(text)) {
            return {
                ...question,
                textResponse: text
            };
        }
        return question;
    },

    /**
     * Handles rating selection for Rating Scale questions.
     * Only applies the change if the question type is RATING_SCALE.
     *
     * @param {object} question - The current question object.
     * @param {object} eventData - The event data.
     * @param {number} eventData.rating - The selected rating value.
     * @returns {object} The updated question object, or the original if the question type does not match.
     */
    [AnswerEvents.RATING_SELECTED]: (question, { rating }) => {
        const questionType = get(question, 'type');
        const ratingValue = get(question, 'ratingValue');
        if (questionType === questionnaireQuestionTypes.RATING_SCALE && !isNil(rating)) {
            return {
                ...question,
                ratingValue: ratingValue === rating ? null : rating
            };
        }
        return question;
    },
};

/**
 * Process an event for a question
 * @param {string} eventName - The event name
 * @param {object} question - The question object
 * @param {object} eventData - The event data
 * @returns {object} - The updated question
 */
export const processEvent = (eventName, question, eventData) => {
    const processor = eventProcessors[eventName];
    
    if (!processor) {
        console.warn(`No processor found for event: ${eventName}`);
        return question;
    }

    return processor(question, eventData);
};

/**
 * Check if a question should trigger immediate save
 * @param {string} questionType - The question type
 * @returns {boolean} - Whether saves should be immediate
 */
export const shouldSaveImmediately = (questionType) => {
    return saveConfigurations[questionType]?.immediate === true;
};

/**
 * Get debounce delay for a question type
 * @param {string} questionType - The question type
 * @returns {number|null} - Debounce delay in ms, or null if immediate
 */
export const getDebounceDelay = (questionType) => {
    return saveConfigurations[questionType]?.debounce || null;
};

/**
 * Convenience object grouping all answer processing functions
 * Usage: import { AnswerProcessor } from './AnswerEventSystem';
 *        AnswerProcessor.selectOption(question, optionId);
 */
export const AnswerProcessor = {
    processEvent
};

 